// A trigger + a portaled, fixed-position panel that closes on an outside
// click, on a resize, or once its trigger is scrolled out of sight.
// Controlled (the caller owns `open`), so a consumer can run its own effects
// off the open/close transition without reaching into this component.
//
// The panel is portaled to document.body (like components/ui/modal.tsx's
// Modal) rather than absolutely positioned inside the trigger's own DOM
// subtree — an unportaled panel gets clipped by any overflow:auto ancestor
// and can lose z-index ties against sibling stacking contexts (e.g. the app
// shell's sticky header). Position is measured from the trigger's rect on
// open, and again on every scroll outside the panel, so the panel follows
// its trigger.
//
// The trigger-relative position alone isn't enough once the panel's own size
// is in play: a trigger near the right/bottom edge of the viewport (e.g. an
// attachment row's trailing button) would otherwise place the panel's own
// far edge — often exactly where its confirm button lives — off-screen. A
// second pass, once the panel itself is in the DOM and measurable, clamps
// left/right to the viewport width and flips the panel above the trigger
// when there isn't room below. A `ResizeObserver` (not just a one-shot
// measurement on open) re-runs that clamp whenever the panel's own content
// changes size — e.g. a loading spinner swapping for the real content once
// an async preview resolves (see IcsAttachmentImport) — since the panel can
// easily grow past the viewport edge well after its first paint.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "@/platform/ui/primitives/cx.js";

const VIEWPORT_MARGIN = 8;

interface TriggerRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

const rectOf = (el: Element): TriggerRect => {
  const r = el.getBoundingClientRect();
  return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
};

/** Whether some of `el` still shows: within the screen and within every
 *  ancestor that clips what overflows it. */
function shows(el: Element): boolean {
  const r = rectOf(el);
  const clip = { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth };
  for (let a = el.parentElement; a; a = a.parentElement) {
    const overflow = getComputedStyle(a).overflow;
    if (!overflow || overflow === "visible") continue;
    const b = rectOf(a);
    clip.top = Math.max(clip.top, b.top);
    clip.left = Math.max(clip.left, b.left);
    clip.bottom = Math.min(clip.bottom, b.bottom);
    clip.right = Math.min(clip.right, b.right);
  }
  return r.bottom > clip.top && r.top < clip.bottom && r.right > clip.left && r.left < clip.right;
}

export function Popover({
  open,
  onOpenChange,
  trigger,
  panelClassName,
  className,
  align = "start",
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  panelClassName?: string;
  className?: string;
  align?: "start" | "end";
  children: ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [triggerRect, setTriggerRect] = useState<TriggerRect | null>(null);
  const [panelSize, setPanelSize] = useState<{ width: number; height: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) {
      setTriggerRect(null);
      setPanelSize(null);
      return;
    }
    setTriggerRect(rectOf(wrapRef.current));
  }, [open]);

  // Measures the panel once it's actually in the DOM (i.e. after the effect
  // above sets a non-null triggerRect and this component re-renders with it)
  // — the first paint below uses the un-clamped guess, and this corrects it
  // before the browser ever shows it. Then keeps watching: the panel's
  // content can change size after that first measurement (a spinner swapping
  // for real content once an async preview resolves), so a ResizeObserver
  // re-measures for as long as the popover stays open, not just once.
  useLayoutEffect(() => {
    if (!triggerRect || !panelRef.current) return;
    const el = panelRef.current;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setPanelSize({ width: r.width, height: r.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [triggerRect]);

  useEffect(() => {
    if (!open) return;
    const follow = (e: Event) => {
      const trigger = wrapRef.current;
      if (!trigger || panelRef.current?.contains(e.target as Node)) return;
      if (!shows(trigger)) return onOpenChange(false);
      const r = rectOf(trigger);
      setTriggerRect((prev) =>
        prev &&
        prev.top === r.top &&
        prev.bottom === r.bottom &&
        prev.left === r.left &&
        prev.right === r.right
          ? prev
          : r,
      );
    };
    const close = () => onOpenChange(false);
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", close);
    };
  }, [open, onOpenChange]);

  let style: { top: number; left?: number; right?: number } | null = null;
  if (triggerRect) {
    const width = panelSize?.width ?? 0;
    const height = panelSize?.height ?? 0;
    let top = triggerRect.bottom + 4;
    if (panelSize && top + height > window.innerHeight - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, triggerRect.top - height - 4);
    }
    if (align === "end") {
      let right = window.innerWidth - triggerRect.right;
      if (panelSize && right + width > window.innerWidth - VIEWPORT_MARGIN) {
        right = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
      }
      style = { top, right };
    } else {
      let left = triggerRect.left;
      if (panelSize && left + width > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
      }
      style = { top, left };
    }
  }

  return (
    <div ref={wrapRef} className={cx("relative", className)}>
      {trigger}
      {open &&
        style &&
        createPortal(
          <>
            {/* On the modal's layer and after it, so an outside click in a
                modal closes the panel rather than reaching the modal. */}
            <div className="fixed inset-0 z-50" onClick={() => onOpenChange(false)} />
            <div ref={panelRef} className={cx("fixed z-50", panelClassName)} style={style}>
              {children}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
